import { useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Alert,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useBrand } from '../../brand/BrandProvider';
import { useTenantSettings, useSaveTenantSettings } from './useTenantSettings';

/**
 * Tenant-Settings — Per-Brand Konfiguration.
 *
 * Route: `/:brandSlug/brand-settings` (single page, kein Sub-Routing)
 * Spec: 01_apis/01_core.api.valitype.net/ADMIN/views.md#tenant-settings
 *
 * 6 Felder gruppiert in 2 Cards: "E-Mail & Branding" und "Compliance & Billing".
 * Save schickt nur Diff der geänderten Felder im PUT-Body.
 */
export function TenantSettingsPage() {
  const brand = useBrand();
  const query = useTenantSettings();
  const save = useSaveTenantSettings();
  const canWrite = useHasPermission({ perms: ['tenantSettings.write'] });

  const original = query.data?.settings;
  const [draft, setDraft] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Sobald die Live-Daten geladen sind, override aus draft nur falls explizit gesetzt
  const view = useMemo(() => ({ ...(original ?? {}), ...draft }), [original, draft]);

  const dirty = useMemo(() => {
    if (!original) return {};
    const out = {};
    Object.entries(draft).forEach(([k, v]) => {
      if (v !== original[k]) out[k] = v;
    });
    return out;
  }, [draft, original]);

  const hasChanges = Object.keys(dirty).length > 0;

  const setField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setSubmitError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!hasChanges) return;
    try {
      await save.mutateAsync(dirty);
      setDraft({});
    } catch (err) {
      setSubmitError(err?.data?.message ?? err?.message ?? 'Speichern fehlgeschlagen.');
    }
  };

  const onDiscard = () => {
    setDraft({});
    setSubmitError(null);
  };

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="Brand-Settings" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="Brand-Settings" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Brand-Settings"
        subtitle={`${brand.currentBrand?.displayName ?? brand.currentBrandOrigin ?? '—'} · GET /v1/admin/tenant-settings`}
      />

      <Alert color="info" className="small">
        Brand-spezifische Konfiguration die nicht in App-Settings lebt. Save
        sendet nur ein Diff der geänderten Felder als <code>PUT</code>.
      </Alert>

      <Form onSubmit={onSubmit}>
        <Card className="mb-3">
          <CardBody>
            <h5 className="mb-3">E-Mail &amp; Branding</h5>

            <FormGroup>
              <Label className="d-flex justify-content-between">
                <span>E-Mail Default-From</span>
                <code className="small text-muted">email_from</code>
              </Label>
              <Input
                type="email"
                value={view.email_from ?? ''}
                onChange={(e) => setField('email_from', e.target.value)}
                placeholder="noreply@brand.com"
                disabled={!canWrite || save.isPending}
              />
              <div className="small text-muted">
                Wird als Absender verwendet, wenn weder
                <code> tenant_credentials.smtp.fromAddress </code>
                noch der Fallback <code>SMTP_FROM_FALLBACK</code> greift.
              </div>
            </FormGroup>

            <FormGroup>
              <Label className="d-flex justify-content-between">
                <span>Logo-URL</span>
                <code className="small text-muted">logo_url</code>
              </Label>
              <Input
                type="url"
                value={view.logo_url ?? ''}
                onChange={(e) => setField('logo_url', e.target.value)}
                placeholder="https://brand.com/logo.svg"
                disabled={!canWrite || save.isPending}
              />
              <div className="small text-muted">
                Eingebettet in E-Mail-Templates (HTML-Head + Header-Block).
              </div>
            </FormGroup>

            <FormGroup>
              <Label className="d-flex justify-content-between">
                <span>Brand-Color</span>
                <code className="small text-muted">brand_color</code>
              </Label>
              <InputGroup>
                <Input
                  type="text"
                  value={view.brand_color ?? ''}
                  onChange={(e) => setField('brand_color', e.target.value)}
                  placeholder="#3f6ad8"
                  disabled={!canWrite || save.isPending}
                  className="font-monospace"
                />
                <InputGroupText
                  style={{
                    background: isValidHex(view.brand_color)
                      ? view.brand_color
                      : 'transparent',
                    minWidth: 60,
                  }}
                  title="Color-Preview"
                >&nbsp;</InputGroupText>
              </InputGroup>
              <div className="small text-muted">
                Hex-Color für CTA-Buttons in E-Mail-Templates.
              </div>
            </FormGroup>

            <FormGroup className="mb-0">
              <Label className="d-flex justify-content-between">
                <span>E-Mail-Template-Subdir</span>
                <code className="small text-muted">email_template</code>
              </Label>
              <Input
                type="text"
                value={view.email_template ?? ''}
                onChange={(e) => setField('email_template', e.target.value)}
                placeholder="valikey"
                maxLength={64}
                disabled={!canWrite || save.isPending}
              />
              <div className="small text-muted">
                Welches Subdir in <code>email/templates/&lt;slug&gt;/</code> die
                Brand für ausgehende Mails nutzt.
              </div>
            </FormGroup>
          </CardBody>
        </Card>

        <Card className="mb-3">
          <CardBody>
            <h5 className="mb-3">Compliance &amp; Billing</h5>

            <FormGroup>
              <Label className="d-flex justify-content-between">
                <span>B2C Widerrufsverzicht Default</span>
                <code className="small text-muted">b2c_default_withdrawal_waived</code>
              </Label>
              <div className="form-check form-switch">
                <Input
                  type="switch"
                  role="switch"
                  id="b2c-waived-switch"
                  checked={!!view.b2c_default_withdrawal_waived}
                  onChange={(e) =>
                    setField('b2c_default_withdrawal_waived', e.target.checked)
                  }
                  disabled={!canWrite || save.isPending}
                />
                <Label check for="b2c-waived-switch" className="ms-2">
                  {view.b2c_default_withdrawal_waived ? 'Ja — Widerruf verzichtet' : 'Nein — 14 Tage Widerrufsrecht'}
                </Label>
              </div>
              <div className="small text-muted mt-2">
                <strong>§ 356 Abs. 5 BGB:</strong> Bei Sofort-Lieferung digitaler
                Inhalte/Services darf der Widerruf verzichtet werden. Aktiv =
                Default <code>withdrawalWaived: true</code> bei B2C-Quotes; das
                Frontend zeigt aber trotzdem den Pflicht-Hinweistext.
              </div>
            </FormGroup>

            <FormGroup className="mb-0">
              <Label className="d-flex justify-content-between">
                <span>Stripe Application-Fee</span>
                <code className="small text-muted">application_fee_bps</code>
              </Label>
              <InputGroup>
                <Input
                  type="number"
                  min={0}
                  max={10_000}
                  step={1}
                  value={view.application_fee_bps ?? ''}
                  onChange={(e) =>
                    setField(
                      'application_fee_bps',
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                  placeholder="100"
                  disabled={!canWrite || save.isPending}
                />
                <InputGroupText>
                  bps ({((view.application_fee_bps ?? 0) / 100).toFixed(2)} %)
                </InputGroupText>
              </InputGroup>
              <div className="small text-muted">
                Plattform-Provision für Stripe-Connect (Basis-Punkte; 100 = 1.00 %).
                Wird beim Stripe-Checkout als
                <code> application_fee_percent </code>
                bzw.
                <code> application_fee_amount </code>
                mitgegeben.
              </div>
            </FormGroup>
          </CardBody>
        </Card>

        {submitError && (
          <Alert color="danger" className="mb-3">{submitError}</Alert>
        )}

        {!canWrite && (
          <Alert color="secondary" className="small">
            Nur-Lese-Modus — Permission <code>tenantSettings.write</code> fehlt.
          </Alert>
        )}

        <HasPermission perms={['tenantSettings.write']}>
          <div className="d-flex justify-content-end gap-2">
            <Button
              type="button"
              color="secondary"
              outline
              onClick={onDiscard}
              disabled={!hasChanges || save.isPending}
            >
              Verwerfen
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!hasChanges || save.isPending}
            >
              {save.isPending
                ? 'Speichern…'
                : `Speichern${hasChanges ? ` (${Object.keys(dirty).length})` : ''}`}
            </Button>
          </div>
        </HasPermission>
      </Form>
    </div>
  );
}

function isValidHex(s) {
  if (typeof s !== 'string') return false;
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s.trim());
}
